import {installedAnimeSources,createAnimeSource} from './AnimeRepository.ts';
import {sourceHttpClient} from '../core/SourceHttp.ts';
import {yomiriDatabase} from '../storage/YomiriDatabase.ts';
import {recommendationMatch,recommendationQueries,searchRecommendation} from '../ui/components/home/RecommendationSearch.ts';
import type {HomeMedia} from '../metadata/HomeRecommendations.ts';
import type {AnimeTitle,AnimeEpisode} from './types.ts';

export interface EpisodeVariant {sourceId:string;sourceName:string;anime:AnimeTitle;episode:AnimeEpisode}
export interface UnifiedEpisode {key:string;number:number;name:string;variants:EpisodeVariant[]}
export interface UnifiedAnimeSnapshot {installed:number;completed:number;episodes:UnifiedEpisode[];issues:{sourceId:string;message:string}[]}
export function groupAnimeEpisodes(variants:EpisodeVariant[]):UnifiedEpisode[] {
  const grouped=new Map<string,UnifiedEpisode>();
  for(const variant of variants){
    const number=variant.episode.episodeNumber;
    // Unknown/special numbering must never collapse unrelated extras into "episode 0".
    const key=Number.isFinite(number)&&number>0?`episode:${number}`:`special:${variant.sourceId}:${variant.episode.id}`;
    const row=grouped.get(key)||{key,number,name:Number.isFinite(number)&&number>0?`Episodio ${number}`:variant.episode.name,variants:[]};
    if(!row.variants.some(item=>item.sourceId===variant.sourceId&&item.episode.id===variant.episode.id))row.variants.push(variant);
    grouped.set(key,row);
  }
  return [...grouped.values()].sort((a,b)=>(a.key.startsWith('special:')?Infinity:a.number)-(b.key.startsWith('special:')?Infinity:b.number)||a.name.localeCompare(b.name));
}
function episodeRequest<T>(job:Promise<T>,signal:AbortSignal):Promise<T>{
  return new Promise((resolve,reject)=>{
    const abort=()=>{clearTimeout(timer);signal.removeEventListener('abort',abort);reject(new Error('Consulta cancelada.'));};
    const timer=setTimeout(()=>{signal.removeEventListener('abort',abort);reject(new Error('La fuente tardó demasiado en responder.'));},4500);
    signal.addEventListener('abort',abort,{once:true});
    if(signal.aborted){abort();return;}
    job.then(resolve,reject).finally(()=>{clearTimeout(timer);signal.removeEventListener('abort',abort);});
  });
}
export async function loadUnifiedAnime(media:HomeMedia,onUpdate:(state:UnifiedAnimeSnapshot)=>void,signal:AbortSignal):Promise<void>{
  const ids=await installedAnimeSources();
  const state:UnifiedAnimeSnapshot={installed:ids.length,completed:0,episodes:[],issues:[]};
  const variants:EpisodeVariant[]=[];let cursor=0;
  const publish=()=>{if(!signal.aborted)onUpdate({...state,episodes:groupAnimeEpisodes(variants),issues:[...state.issues]});};
  publish();
  async function worker(){
    while(cursor<ids.length&&!signal.aborted){
      const sourceId=ids[cursor++];
      try{
        const source=createAnimeSource(sourceId,sourceHttpClient);
        const confirmed=await yomiriDatabase.getSetting<AnimeTitle>(`unified-link:${media.id}:${sourceId}`);
        const old=await yomiriDatabase.getSetting<AnimeTitle>(`home-link:ANIME:${media.id}`);
        let anime=confirmed?.sourceId===sourceId?confirmed:old?.sourceId===sourceId&&recommendationMatch(old,media)===3?old:undefined;
        if(!anime){
          for(const query of recommendationQueries(media,media.title.romaji)){
            if(signal.aborted)return;
            const results=await searchRecommendation('ANIME',sourceId,query);
            const exact=results.filter(row=>recommendationMatch(row,media)===3) as AnimeTitle[];
            const unique=[...new Map(exact.map(row=>[row.id,row])).values()];
            if(unique.length===1){anime=unique[0];void yomiriDatabase.setSetting(`unified-link:${media.id}:${sourceId}`,anime).catch(()=>{});break;}
            if(unique.length>1)throw new Error('Hay varias ediciones con el mismo título. Confirmá la coincidencia.');
          }
        }
        if(!anime)throw new Error('No hay una coincidencia exacta; podés vincularla manualmente.');
        if(signal.aborted)return;
        const extra=await yomiriDatabase.getSetting<AnimeTitle[]>(`confirmed-links:ANIME:${media.id}`,[])||[];
        const matches=[...new Map([anime,...extra.filter(row=>row.sourceId===sourceId)].map(row=>[row.id,row])).values()];
        const episodes=await episodeRequest(source.getEpisodes(anime.id),signal);
        for(const additional of matches.filter(row=>row.id!==anime!.id)){const additionalEpisodes=await episodeRequest(source.getEpisodes(additional.id),signal);if(signal.aborted)return;variants.push(...additionalEpisodes.map(episode=>({sourceId,sourceName:source.name,anime:additional,episode})));}
        if(signal.aborted)return;
        variants.push(...episodes.map(episode=>({sourceId,sourceName:source.name,anime:anime!,episode})));
      }catch(error){state.issues.push({sourceId,message:error instanceof Error?error.message:'La fuente no respondió.'});}
      state.completed++;publish();
    }
  }
  await Promise.all([worker(),worker()]);
}
export function unifiedEpisodeRoute(variant:EpisodeVariant,mediaId:number){
  history.pushState({},'','/?'+new URLSearchParams({section:'anime',animeSource:variant.sourceId,anime:variant.anime.id,episode:variant.episode.id,media:String(mediaId)}));
  window.dispatchEvent(new PopStateEvent('popstate'));
}
